import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDateTime, fmtMoney, fmtNum } from "@/lib/api";

// Compact chip that shows the V2 verdict for a specific trade. Green when
// V2 would have taken the trade, amber when V2 would have skipped it, gray
// for unmatched (trade has no linked signal to evaluate).
function V2VerdictChip({ verdict }) {
  if (!verdict) {
    return (
      <span
        className="text-[10px] uppercase tracking-[0.15em] text-slate-600"
        title="No linked signal — V2 verdict unavailable"
        data-testid="v2-chip-unlinked"
      >
        —
      </span>
    );
  }
  const { v2_status, v2_rejection_code } = verdict;
  if (v2_status === "taken") {
    return (
      <span
        className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-700"
        title="V2 would have taken this trade"
        data-testid="v2-chip-taken"
      >
        TAKEN
      </span>
    );
  }
  if (v2_status === "avoided") {
    return (
      <span
        className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-700 cursor-help font-mono"
        title={`V2 would have SKIPPED this trade — ${v2_rejection_code || "UNSPECIFIED"}`}
        data-testid="v2-chip-avoided"
      >
        {v2_rejection_code
          ? v2_rejection_code.replace(/_/g, " ").slice(0, 18)
          : "SKIPPED"}
      </span>
    );
  }
  // no_signal_link / verdict_error
  return (
    <span
      className="text-[10px] uppercase tracking-[0.15em] text-slate-500"
      title={`V2 verdict: ${v2_status}`}
      data-testid="v2-chip-other"
    >
      {v2_status}
    </span>
  );
}

export default function TradeHistory() {
  const [rows, setRows] = useState([]);
  const [verdictByTrade, setVerdictByTrade] = useState({});

  useEffect(() => {
    // Fetch trades and V2 verdicts in parallel. If v2-shadow fails (e.g.
    // older backend), we still render the trades without the column.
    Promise.all([
      api.get("/trades"),
      api
        .get("/strategy/v2-shadow", { params: { days: 365 } })
        .catch(() => ({ data: null })),
    ]).then(([tradesRes, shadowRes]) => {
      setRows(tradesRes.data || []);
      const list =
        (shadowRes.data && (shadowRes.data.per_trade || shadowRes.data.recent)) ||
        [];
      const map = {};
      for (const row of list) {
        if (row.trade_id) {
          map[row.trade_id] = {
            v2_status: row.v2_status,
            v2_rejection_code: row.v2_rejection_code,
          };
        }
      }
      setVerdictByTrade(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(() => {
    let taken = 0,
      avoided = 0,
      unlinked = 0;
    for (const r of rows) {
      const v = verdictByTrade[r.id];
      if (!v) unlinked++;
      else if (v.v2_status === "taken") taken++;
      else if (v.v2_status === "avoided") avoided++;
      else unlinked++;
    }
    return { taken, avoided, unlinked };
  }, [rows, verdictByTrade]);

  return (
    <div className="space-y-4" data-testid="trades-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Trade History</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
            All completed paper trades with charges &amp; net P&amp;L
          </p>
        </div>
        {rows.length > 0 && (
          <div
            className="flex items-center gap-3 text-[10px] uppercase tracking-[0.15em]"
            data-testid="v2-verdict-summary"
          >
            <span className="text-emerald-300">
              <span className="font-mono">{summary.taken}</span> taken
            </span>
            <span className="text-amber-300">
              <span className="font-mono">{summary.avoided}</span> skipped
            </span>
            {summary.unlinked > 0 && (
              <span className="text-slate-500">
                <span className="font-mono">{summary.unlinked}</span> unlinked
              </span>
            )}
          </div>
        )}
      </div>

      <div className="surface rounded-sm overflow-x-auto">
        <table className="dt">
          <thead>
            <tr>
              <th>Opened</th>
              <th>Closed</th>
              <th>Symbol</th>
              <th>Type</th>
              <th className="num">Lots</th>
              <th className="num">Entry</th>
              <th className="num">Exit</th>
              <th>Exit Reason</th>
              <th className="num">Gross P&amp;L</th>
              <th className="num">Entry Chg</th>
              <th className="num">Exit Chg</th>
              <th className="num">Total Chg</th>
              <th className="num">Net P&amp;L</th>
              <th className="num">MAE</th>
              <th className="num">MFE</th>
              <th>V2 Verdict</th>
              <th>Src</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid="trade-row">
                <td className="font-mono text-slate-400">{fmtDateTime(r.opened_at)}</td>
                <td className="font-mono text-slate-300">{fmtDateTime(r.closed_at)}</td>
                <td className="font-mono">{r.tradingsymbol}</td>
                <td>{r.option_type}</td>
                <td className="num font-mono">{r.lots}</td>
                <td className="num font-mono">{fmtNum(r.entry_price)}</td>
                <td className="num font-mono">{fmtNum(r.exit_price)}</td>
                <td className="text-[10px] uppercase tracking-[0.15em]">{r.exit_reason}</td>
                <td className={`num font-mono ${r.gross_pnl >= 0 ? "text-pos" : "text-neg"}`}>{fmtMoney(r.gross_pnl)}</td>
                <td className="num font-mono text-slate-400">{fmtMoney(r.entry_charges)}</td>
                <td className="num font-mono text-slate-400">{fmtMoney(r.exit_charges)}</td>
                <td className="num font-mono text-slate-300">{fmtMoney(r.total_charges)}</td>
                <td className={`num font-mono font-semibold ${r.net_pnl >= 0 ? "text-pos" : "text-neg"}`}>{fmtMoney(r.net_pnl)}</td>
                <td className="num font-mono text-neg">{r.mae != null ? fmtMoney(r.mae) : "—"}</td>
                <td className="num font-mono text-pos">{r.mfe != null ? fmtMoney(r.mfe) : "—"}</td>
                <td>
                  <V2VerdictChip verdict={verdictByTrade[r.id]} />
                </td>
                <td className="text-[10px] text-slate-500">
                  {r.charge_source === "KITE_VIRTUAL_CONTRACT_NOTE" ? "KITE" : "FALLBACK"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan="17" className="text-center text-slate-500 py-10">
                  No trades yet. Run a demo session from the Dashboard.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
